"use client";

import { useState, useCallback } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { CategoryTabs } from "@/components/pos/CategoryTabs";
import { MenuGrid } from "@/components/pos/MenuGrid";
import { OrderPanel } from "@/components/pos/OrderPanel";
import { ModifierModal } from "@/components/pos/ModifierModal";
import { AICommandBar } from "@/components/pos/AICommandBar";
import { PaymentModal } from "@/components/pos/PaymentModal";
import { QuickServicePOS } from "@/components/pos/QuickServicePOS";
import { JewelryPOS } from "@/components/pos/JewelryPOS";
import { useCategories, useMenuItems, useMenuItem } from "@/hooks/useMenu";
import { usePOS } from "@/hooks/usePOS";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useAIAgent } from "@/hooks/useAIAgent";
import { useBusinessType } from "@/hooks/useBusinessType";
import { toast } from "sonner";
import Link from "next/link";

export default function POSPage() {
  const { businessType, loading: businessTypeLoading, isQuickService, isJewelry } = useBusinessType();

  if (businessTypeLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-black">
        <Spinner size="lg" />
      </div>
    );
  }

  // Jewelry: serialized inventory, clienteling-first POS
  if (isJewelry) {
    return <JewelryPOS />;
  }

  // Quick service (CAFE, FOOD_TRUCK): simplified layout
  if (isQuickService) {
    return <QuickServicePOS businessType={businessType as "RESTAURANT" | "CAFE" | "BAR" | "FOOD_TRUCK"} />;
  }

  // Full-service restaurant/bar layout
  return <FullServicePOS />;
}

function FullServicePOS() {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);
  const [lastOrderNumber, setLastOrderNumber] = useState<string>("");
  const [showPayment, setShowPayment] = useState(false);
  const [lastOrderTotal, setLastOrderTotal] = useState(0);

  const { categories, loading: categoriesLoading } = useCategories();
  const { items, loading: itemsLoading } = useMenuItems({
    categoryId: selectedCategoryId,
  });
  const { item: selectedItemDetail } = useMenuItem(selectedItemId);

  const pos = usePOS();

  // AI Agent
  const agent = useAIAgent({
    onAction: useCallback((action: { type: string; data: Record<string, unknown> }) => {
      switch (action.type) {
        case "add_item": {
          const { menuItemId, name, price, quantity = 1 } = action.data as {
            menuItemId: string;
            name: string;
            price: number;
            quantity?: number;
          };
          for (let i = 0; i < quantity; i++) {
            pos.addItem({ menuItemId, name, price });
          }
          toast.success(`Added ${quantity}x ${name}`);
          break;
        }
        case "set_order_type":
          pos.setOrderType(action.data.type as string);
          toast.success(`Order type: ${action.data.type}`);
          break;
        case "set_table":
          pos.setTable(null, action.data.tableNumber as string);
          toast.success(`Table ${action.data.tableNumber}`);
          break;
        case "send_to_kitchen":
          handleSendToKitchen();
          break;
        case "clear_order":
          pos.clearOrder();
          toast.success("Order cleared");
          break;
      }
    }, []),
  });

  // Voice Input
  const voice = useVoiceInput({
    onResult: (transcript) => {
      agent.sendMessage(transcript, {
        currentOrder: pos.items,
        orderType: pos.orderType,
        tableNumber: pos.tableNumber,
      });
    },
  });

  const handleItemClick = (item: { id: string; name: string; price: number }) => {
    setSelectedItemId(item.id);
  };

  const handleAddToOrder = (
    modifiers: Array<{ modifierId: string; name: string; price: number }>,
    quantity: number,
    notes: string
  ) => {
    if (!selectedItemDetail) return;
    for (let i = 0; i < quantity; i++) {
      pos.addItem({
        menuItemId: selectedItemDetail.id,
        name: selectedItemDetail.name,
        price: selectedItemDetail.price,
        modifiers,
        notes,
      });
    }
    setSelectedItemId(null);
  };

  const handleQuickAdd = (item: { id: string; name: string; price: number }) => {
    pos.addItem({
      menuItemId: item.id,
      name: item.name,
      price: item.price,
    });
  };

  const handleSendToKitchen = async () => {
    try {
      const order = await pos.sendToKitchen();
      if (order) {
        const orderId = order.data?.id || order.id;
        const orderNum = order.data?.orderNumber || order.orderNumber || "";
        const orderTotal = order.data?.totalAmount || order.totalAmount || 0;
        setLastOrderId(orderId);
        setLastOrderNumber(orderNum);
        setLastOrderTotal(orderTotal);
        toast.success("Order sent to workshop!");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send order");
    }
  };

  const handlePay = () => {
    if (lastOrderId) {
      setShowPayment(true);
    }
  };

  const handlePaymentComplete = () => {
    setShowPayment(false);
    setLastOrderId(null);
    setLastOrderNumber("");
    setLastOrderTotal(0);
    toast.success("Payment complete!");
  };

  const onItemClick = (item: { id: string; name: string; price: number }) => {
    handleItemClick(item);
  };

  // Check if the fetched item has modifier groups
  const showModifierModal = selectedItemId && selectedItemDetail &&
    selectedItemDetail.modifierGroups && selectedItemDetail.modifierGroups.length > 0;

  // If item has no modifiers, quick add and close
  if (selectedItemId && selectedItemDetail &&
    (!selectedItemDetail.modifierGroups || selectedItemDetail.modifierGroups.length === 0)) {
    handleQuickAdd(selectedItemDetail);
    setSelectedItemId(null);
  }

  const handleAISubmit = (message: string) => {
    agent.sendMessage(message, {
      currentOrder: pos.items,
      orderType: pos.orderType,
      tableNumber: pos.tableNumber,
    });
  };

  const handleMicClick = () => {
    if (voice.isListening) {
      voice.stopListening();
    } else {
      voice.startListening();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-black">
      {/* Top bar — minimal, 48px */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-white/8 shrink-0">
        <div className="flex items-center gap-3">
          <Link href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <span className="text-xs font-bold text-black">NX</span>
            </div>
            <span className="text-sm font-semibold text-white">Facet</span>
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {pos.orderType.replace("_", " ")}
          </span>
          {pos.tableNumber && (
            <span className="rounded-lg bg-white/8 px-2.5 py-1 text-xs font-medium text-white">
              Table {pos.tableNumber}
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-success">Live</span>
          </div>
        </div>
      </div>

      {/* Main content — split view */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Menu area (60%) */}
        <div className="flex flex-col flex-[3] min-w-0 border-r border-white/5">
          {/* Category tabs */}
          <div className="px-4 pt-3 pb-2 shrink-0">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-3">
                <Spinner size="sm" />
              </div>
            ) : (
              <CategoryTabs
                categories={categories.map((c) => ({
                  id: c.id,
                  name: c.name,
                  color: c.color || undefined,
                  itemCount: c._count.items,
                }))}
                selectedId={selectedCategoryId}
                onSelect={setSelectedCategoryId}
              />
            )}
          </div>

          {/* Menu grid */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <MenuGrid
              items={items.filter((i) => i.isActive).map((i) => ({
                id: i.id,
                name: i.name,
                price: i.price,
                image: i.image || undefined,
                isPopular: i.isPopular,
                itemType: i.itemType,
              }))}
              onItemClick={onItemClick}
              loading={itemsLoading}
            />
          </div>
        </div>

        {/* Right: Order panel (40%) */}
        <div className="flex-[2] flex flex-col min-w-[300px] max-w-[420px]">
          <OrderPanel
            items={pos.items}
            orderType={pos.orderType}
            tableNumber={pos.tableNumber || undefined}
            onUpdateQuantity={pos.updateQuantity}
            onRemoveItem={pos.removeItem}
            onClearOrder={() => {
              pos.clearOrder();
              setLastOrderId(null);
            }}
            onSendToKitchen={handleSendToKitchen}
            onPay={handlePay}
            onChangeOrderType={pos.setOrderType}
            subtotal={pos.subtotal}
            taxAmount={pos.taxAmount}
            total={lastOrderId ? lastOrderTotal : pos.total}
            loading={pos.sendingToKitchen}
            orderSent={!!lastOrderId}
          />
        </div>
      </div>

      {/* Bottom: AI Command Bar */}
      <AICommandBar
        onSubmit={handleAISubmit}
        loading={agent.loading}
        isListening={voice.isListening}
        isVoiceSupported={voice.isSupported}
        transcript={voice.transcript}
        interimTranscript={voice.interimTranscript}
        onMicClick={handleMicClick}
        lastMessage={agent.lastResponse?.message}
        suggestions={agent.suggestions}
        onSuggestionClick={handleAISubmit}
      />

      {/* Modifier Modal */}
      {showModifierModal && selectedItemDetail && (
        <ModifierModal
          item={{
            name: selectedItemDetail.name,
            price: selectedItemDetail.price,
            modifierGroups: selectedItemDetail.modifierGroups || [],
          }}
          onConfirm={handleAddToOrder}
          onClose={() => setSelectedItemId(null)}
        />
      )}

      {/* Payment Modal */}
      {showPayment && lastOrderId && (
        <PaymentModal
          orderId={lastOrderId}
          orderNumber={lastOrderNumber}
          total={lastOrderTotal}
          onClose={() => setShowPayment(false)}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
}
